import { toBase64 } from 'sharedKernel/file/handler'
import { IMeetingRoomFile, IMeetingRoomFormData, IMeetingRoomImage } from '../types'
import {
  MEETING_ROOM_COVER_FILE_TYPE,
  MEETING_ROOM_EMPTY_GUID,
  MEETING_ROOM_SLIDER_FILE_TYPE,
  MEETING_ROOM_SUPPORT_FILE_TYPE,
} from './constants'
import { MeetingRoomFileDto } from './types'

export type MeetingRoomUploadFileType =
  | typeof MEETING_ROOM_COVER_FILE_TYPE
  | typeof MEETING_ROOM_SLIDER_FILE_TYPE
  | typeof MEETING_ROOM_SUPPORT_FILE_TYPE

const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
}

export const resolveMeetingRoomFileMimeType = (file: File): string => {
  if (file.type.trim() !== '') {
    return file.type
  }
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  return MIME_BY_EXTENSION[extension] ?? 'application/octet-stream'
}

const fileExtensionFromName = (name: string): string => name.split('.').pop()?.toLowerCase() ?? ''

const readBase64Content = async (file: File): Promise<string> => {
  const fileContent = await toBase64(file)
  if (typeof fileContent !== 'string') return ''
  const commaIndex = fileContent.indexOf(',')
  return commaIndex >= 0 ? fileContent.substring(commaIndex + 1) : fileContent
}

export const mapNewFileToSaveDto = async (
  file: File,
  fileType: MeetingRoomUploadFileType
): Promise<MeetingRoomFileDto> => {
  const extension = fileExtensionFromName(file.name)
  return {
    id: MEETING_ROOM_EMPTY_GUID,
    mimeType: resolveMeetingRoomFileMimeType(file),
    originalName: file.name,
    extension,
    size: file.size,
    fileType,
    content: await readBase64Content(file),
  }
}

const mapRemovedFileToSaveDto = (file: {
  id: string
  originalName?: string
  name?: string
  fileType?: string
}): MeetingRoomFileDto => ({
  id: file.id,
  originalName: file.originalName ?? file.name ?? '',
  fileType: file.fileType,
  isDeleted: true,
})

export const getMeetingRoomRemovedFileIds = (form: IMeetingRoomFormData): string[] => [
  ...(form.removedCoverImageIds ?? []),
  ...(form.removedSliderImageIds ?? []),
  ...(form.removedSupportFileIds ?? []),
]

export const buildMeetingRoomFilesForSave = async (
  form: IMeetingRoomFormData
): Promise<{ images: MeetingRoomFileDto[]; supportFiles: MeetingRoomFileDto[] }> => {
  const images: MeetingRoomFileDto[] = []

  for (const file of form.coverImages ?? []) {
    images.push(await mapNewFileToSaveDto(file, MEETING_ROOM_COVER_FILE_TYPE))
  }

  for (const imageId of form.removedCoverImageIds ?? []) {
    const image = form.existingCoverImages?.find(item => item.id === imageId)
    images.push(
      mapRemovedFileToSaveDto({
        id: imageId,
        name: image?.name,
        fileType: MEETING_ROOM_COVER_FILE_TYPE,
      })
    )
  }

  for (const file of form.sliderImages ?? []) {
    images.push(await mapNewFileToSaveDto(file, MEETING_ROOM_SLIDER_FILE_TYPE))
  }

  for (const imageId of form.removedSliderImageIds ?? []) {
    const image = form.existingSliderImages?.find(item => item.id === imageId)
    images.push(
      mapRemovedFileToSaveDto({
        id: imageId,
        name: image?.name,
        fileType: MEETING_ROOM_SLIDER_FILE_TYPE,
      })
    )
  }

  const supportFiles: MeetingRoomFileDto[] = []

  if (form.rulesFile) {
    supportFiles.push(await mapNewFileToSaveDto(form.rulesFile, MEETING_ROOM_SUPPORT_FILE_TYPE))
  }

  for (const fileId of form.removedSupportFileIds ?? []) {
    const file = form.existingSupportFiles?.find(item => item.id === fileId)
    supportFiles.push(
      mapRemovedFileToSaveDto({
        id: fileId,
        originalName: file?.originalName,
        name: file?.originalName,
        fileType: MEETING_ROOM_SUPPORT_FILE_TYPE,
      })
    )
  }

  return { images, supportFiles }
}

export const mapUploadedFileToImage = (file: IMeetingRoomFile): IMeetingRoomImage => ({
  id: file.id,
  path: file.path,
  name: file.originalName ?? file.path,
})
